'use client'

import React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { motion } from 'framer-motion'
import { AnimatedNumber } from '@/components/ui/animated-number'
import {
  CheckmarkFilled as CheckCircle2,
  ArrowRight,
  ChevronDown,
  Security as Shield,
  Lightning as Zap,
  Group as Users,
  DataBase as Database,
  Task as FileCheck,
  Renew as RefreshCw,
  ChartColumn as BarChart3,
  Mobile as Smartphone,
  Link as Link2,
  Location as MapPin,
  CheckboxChecked as CheckSquare,
  Upload,
  Send,
  Time as Clock,
  Target,
  Trophy,
  Locked as Lock,
  WarningFilled as AlertCircle,
  Phone,
  Email as Mail,
  Chat as MessageSquare,
  Enterprise as Building2,
  Education as GraduationCap,
  Badge as Award,
  Growth as TrendingUp,
  AiGenerate as Sparkles,
  Document as FileText,
  Cloud,
  SecurityServices as ShieldCheck,
  Activity,
  Chip as Cpu,
  Globe,
  Book as BookOpen,
  Layers,
  Rocket,
} from '@carbon/icons-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Breadcrumbs } from '@/components/breadcrumbs'
import { siteConfig } from '@/config/site'
import { cn } from '@/lib/utils'
import { UDISE_APAAR_FAQS } from './faqs'

/* The FAQ below is a <details> list rather than the Radix accordion this page
   used to render. Radix unmounts a closed panel, so the answers reached the
   delivered HTML only after a click — and this page now emits FAQPage JSON-LD
   for exactly those answers. A <details> ships them collapsed but present, so
   the markup describes text that is genuinely on the page. Same contract the
   /schoolerp FAQ works to. */
const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  '@id': `${siteConfig.url}/udise-apaar#faq`,
  mainEntity: UDISE_APAAR_FAQS.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: { '@type': 'Answer', text: faq.answer },
  })),
}

export default function UdiseApaarPage() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden hero-dark-bg text-foreground">
        <div className="container relative py-16 lg:py-24 z-10">
          <div className="mx-auto max-w-7xl">
            <Breadcrumbs
              items={[
                { name: 'Home', href: '/' },
                { name: 'UDISE+ & APAAR ID', href: '/udise-apaar' },
              ]}
              className="mb-8 flex justify-center"
            />

            <div className="text-center space-y-8">
              <div className="space-y-6">
                {/* Trust Badges */}
                <div className="flex justify-center gap-4">
                  <Badge className="bg-emerald-600/10 dark:bg-primary-active/10 text-emerald-600 dark:text-primary-active border-emerald-600/30 dark:border-primary-active/30">
                    <ShieldCheck className="mr-2 h-4 w-4" />
                    ISO 27001 Certified
                  </Badge>
                  <Badge className="bg-emerald-600/10 dark:bg-primary-active/10 text-emerald-600 dark:text-primary-active border-emerald-600/30 dark:border-primary-active/30">
                    <Award className="mr-2 h-4 w-4" />
                    Government Approved
                  </Badge>
                </div>

                <p className="text-sm font-medium uppercase tracking-wider text-emerald-600 dark:text-primary-active">
                  UDISE+ & APAAR COMPLIANCE
                </p>
                {/* The h1 used to open on "100% Government Compliant", which
                    is a claim rather than a name: nobody types it into a search
                    box, and it pushed the two terms this page is actually found
                    by into the second and third lines. The three-tier treatment
                    is unchanged — only the words moved. */}
                <h1 className="font-heading text-5xl font-semibold tracking-tight lg:text-6xl xl:text-7xl">
                  UDISE+ Compliance Software
                  <span className="text-emerald-600 dark:text-primary-active block mt-2">
                    with Complete APAAR ID Integration
                  </span>
                  <span className="text-3xl md:text-4xl lg:text-5xl block mt-2">
                    for Indian Schools
                  </span>
                </h1>
                <p className="text-lg leading-relaxed text-muted-foreground lg:text-xl max-w-4xl mx-auto">
                  Euclideum seamlessly integrates with UDISE+ and APAAR ID
                  systems, ensuring your school stays compliant with government
                  regulations while automating data submission and student
                  identity management.
                </p>
              </div>
              {/* Both buttons were dead: no href, no handler. They now point
                  at the two routes a reader on this page actually wants. */}
              <div className="flex flex-wrap gap-4 justify-center">
                <Button asChild size="lg" className="font-medium">
                  <Link href="/schedule-meeting">
                    Get UDISE+ Ready
                    <ArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link href="/docs">
                    <FileText className="mr-2 size-4" />
                    View Documentation
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Real-time Dashboard Metrics */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <Badge className="mb-4">Live Dashboard</Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4 text-foreground dark:text-white">
                UDISE+ Dashboard
              </h2>
              <p className="text-lg text-muted-foreground dark:text-gray-400 max-w-3xl mx-auto">
                Real-time metrics showing our platform's impact on educational
                compliance across India
              </p>
            </motion.div>

            {/* Dashboard Widget */}
            <Card className="p-8 bg-gradient-to-br from-[#141417] to-[#323238] text-white border-0 overflow-hidden relative">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary-active/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary-active/10 rounded-full blur-3xl" />

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">
                      UDISE+ Compliance Status
                    </h3>
                    <p className="text-gray-300">
                      Real-time synchronization active
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-primary-active rounded-full animate-pulse" />
                    <span className="text-sm text-gray-300">Live</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {[
                    {
                      icon: Building2,
                      value: '10,000+',
                      label: 'Schools Compliant',
                      trend: '+12%',
                    },
                    {
                      icon: GraduationCap,
                      value: '1M+',
                      label: 'APAAR IDs Generated',
                      trend: '+25%',
                    },
                    {
                      icon: Target,
                      value: '100%',
                      label: 'Accuracy Rate',
                      trend: 'Maintained',
                    },
                    {
                      icon: Activity,
                      value: '24/7',
                      label: 'Auto Sync',
                      trend: 'Active',
                    },
                  ].map((stat, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white/10 backdrop-blur-sm rounded-xl p-4"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <stat.icon className="w-8 h-8 text-primary-active" />
                        <span className="text-xs text-primary-active">
                          {stat.trend}
                        </span>
                      </div>
                      <div className="text-3xl font-bold mb-1">
                        <AnimatedNumber
                          value={stat.value}
                          delay={index * 100}
                        />
                      </div>
                      <div className="text-sm text-gray-300">{stat.label}</div>
                    </motion.div>
                  ))}
                </div>

                {/* Additional Metrics */}
                <div className="mt-8 pt-8 border-t border-white/20">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                    <div>
                      <div className="text-sm text-muted-foreground/80 mb-1">
                        Data Completion
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-2xl font-bold text-primary-active">
                          <AnimatedNumber value="98%" />
                        </div>
                        <div className="flex-1 h-2 bg-white/20 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary-active rounded-full"
                            style={{ width: '98%' }}
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground/80 mb-1">
                        Students Synced
                      </div>
                      <div className="text-2xl font-bold text-primary-active">
                        <AnimatedNumber value="2,512" />
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground/80 mb-1">
                        New APAAR IDs Today
                      </div>
                      <div className="text-2xl font-bold text-primary-active">
                        <AnimatedNumber value="245" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* NEP 2020 Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 text-center"
            >
              <Badge className="bg-gradient-to-r from-orange-500 to-green-500 text-white border-0 px-6 py-2">
                <BookOpen className="mr-2 h-4 w-4" />
                NEP 2020 Compliant
              </Badge>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Features Grid */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <Badge className="mb-4">Complete Integration</Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4 text-foreground dark:text-white">
                Complete UDISE+ & APAAR Integration
              </h2>
              <p className="text-lg text-muted-foreground dark:text-gray-400 max-w-3xl mx-auto">
                Everything you need for seamless government compliance in one
                unified platform
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: RefreshCw,
                  title: 'Auto UDISE+ Sync',
                  description:
                    'Real-time data submission to UDISE+ portal without manual intervention',
                  highlight: true,
                },
                {
                  icon: Sparkles,
                  title: 'APAAR ID Generation',
                  description:
                    'Bulk creation and management of APAAR IDs for all students',
                  highlight: true,
                },
                {
                  icon: BookOpen,
                  title: 'NEP 2020 Ready',
                  description:
                    'Built-in compliance with National Education Policy guidelines',
                  highlight: false,
                },
                {
                  icon: FileCheck,
                  title: 'Error-Free Submission',
                  description:
                    'Automated validation ensures 100% accurate data submission',
                  highlight: false,
                },
                {
                  icon: Database,
                  title: 'Unified Data Management',
                  description:
                    'Single source of truth for all student and institutional data',
                  highlight: false,
                },
                {
                  icon: Zap,
                  title: 'Real-Time Sync',
                  description:
                    'Continuous synchronization without manual data entry',
                  highlight: false,
                },
                {
                  icon: FileText,
                  title: 'Auto Reports',
                  description:
                    'Generate reports in prescribed UDISE+ formats automatically',
                  highlight: false,
                },
                {
                  icon: BarChart3,
                  title: 'Analytics Dashboard',
                  description:
                    'Track submission status and compliance metrics in real-time',
                  highlight: false,
                },
                {
                  icon: Smartphone,
                  title: 'Mobile Ready',
                  description:
                    'Update data and track compliance from anywhere, anytime',
                  highlight: false,
                },
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                >
                  <Card
                    className={cn(
                      'p-6 h-full border-2 hover:shadow-xl  transition-all duration-300',
                      feature.highlight
                        ? 'border-emerald-600/50 dark:border-primary-active/50 bg-gradient-to-br from-emerald-600/5 dark:from-primary-active/5 to-transparent'
                        : 'hover:border-emerald-600/30 dark:hover:border-primary-active/30'
                    )}
                  >
                    <div
                      className={cn(
                        'w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-colors',
                        feature.highlight
                          ? 'bg-emerald-600/20 dark:bg-primary-active/20'
                          : 'bg-muted group-hover:bg-emerald-600/10 dark:group-hover:bg-primary-active/10'
                      )}
                    >
                      <feature.icon
                        className={cn(
                          'w-6 h-6',
                          feature.highlight
                            ? 'text-emerald-600 dark:text-primary-active'
                            : 'text-muted-foreground dark:text-gray-400 group-hover:text-emerald-600 dark:group-hover:text-primary-active'
                        )}
                      />
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-foreground dark:text-white">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground dark:text-gray-400">
                      {feature.description}
                    </p>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 5-Step Implementation Process */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <Badge className="mb-4">Simple Process</Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4 text-foreground dark:text-white">
                Simplified 5-Step Process
              </h2>
              <p className="text-lg text-muted-foreground dark:text-gray-400 max-w-3xl mx-auto">
                Get compliant with UDISE+ in just 5 simple steps
              </p>
            </motion.div>

            <div className="relative">
              {/* Connection Line */}
              <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-gray-300 via-emerald-600 to-gray-300 dark:from-gray-700 dark:via-primary-active dark:to-gray-700 hidden lg:block" />

              <div className="grid md:grid-cols-5 gap-6 relative">
                {[
                  {
                    step: '1',
                    icon: Link2,
                    title: 'Connect',
                    description: 'Link Euclideum with UDISE+ portal',
                  },
                  {
                    step: '2',
                    icon: MapPin,
                    title: 'Map',
                    description: 'Auto-map data fields',
                  },
                  {
                    step: '3',
                    icon: CheckSquare,
                    title: 'Validate',
                    description: 'Automatic data verification',
                  },
                  {
                    step: '4',
                    icon: Sparkles,
                    title: 'Generate',
                    description: 'Create APAAR IDs in bulk',
                  },
                  {
                    step: '5',
                    icon: Send,
                    title: 'Submit',
                    description: 'One-click submission to UDISE+',
                  },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="relative"
                  >
                    <Card className="p-6 text-center bg-card border-2 hover:border-emerald-600/50 dark:hover:border-primary-active/50 transition-all duration-300">
                      <div className="w-12 h-12 rounded-full bg-emerald-600 dark:bg-primary-active text-white dark:text-[#141417] font-bold text-lg flex items-center justify-center mx-auto mb-4 relative z-10">
                        {item.step}
                      </div>
                      <item.icon className="w-8 h-8 text-emerald-600 dark:text-primary-active mx-auto mb-3" />
                      <h3 className="font-bold text-lg mb-2 text-foreground dark:text-white">
                        {item.title}
                      </h3>
                      <p className="text-sm text-muted-foreground dark:text-gray-400">
                        {item.description}
                      </p>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-background">
        <div className="container px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8 sm:mb-12"
            >
              <Badge className="mb-3 sm:mb-4 text-xs sm:text-sm">
                Transform Your Process
              </Badge>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-3 sm:mb-4 text-foreground dark:text-white">
                Transform Your Compliance Process
              </h2>
              <p className="text-sm sm:text-base lg:text-lg text-muted-foreground dark:text-gray-400 max-w-3xl mx-auto">
                Experience the power of automation and see immediate results
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
              {[
                {
                  icon: Clock,
                  metric: '90%',
                  label: 'Time Saved',
                  description:
                    'Reduce compliance workload from days to minutes',
                  color: 'text-blue-500',
                },
                {
                  icon: Target,
                  metric: '100%',
                  label: 'Accuracy',
                  description:
                    'Zero errors with automated validation and checks',
                  color: 'text-emerald-600 dark:text-primary-active',
                },
                {
                  icon: TrendingUp,
                  metric: '5x',
                  label: 'Productivity',
                  description: 'Focus on education while we handle compliance',
                  color: 'text-purple-500',
                },
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="p-4 sm:p-6 lg:p-8 text-center border-2 hover:border-emerald-600/30 dark:hover:border-primary-active/30 hover:shadow-xl transition-all duration-300">
                    <benefit.icon
                      className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 ${benefit.color} mx-auto mb-2 sm:mb-3 lg:mb-4`}
                    />
                    <div
                      className={`text-3xl sm:text-4xl lg:text-5xl font-bold ${benefit.color} mb-1 sm:mb-2`}
                    >
                      <AnimatedNumber
                        value={benefit.metric}
                        delay={index * 100}
                      />
                    </div>
                    <h3 className="text-base sm:text-lg lg:text-xl font-bold mb-1 sm:mb-2 text-foreground dark:text-white">
                      {benefit.label}
                    </h3>
                    <p className="text-xs sm:text-sm lg:text-base text-muted-foreground dark:text-gray-400">
                      {benefit.description}
                    </p>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Success Story */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-gray-50 to-background dark:from-gray-900 dark:to-gray-950">
        <div className="container px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <Badge className="mb-4">Success Story</Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4 text-foreground dark:text-white">
                Walnut School Achieves 100% UDISE+ Compliance
              </h2>
            </motion.div>

            <Card className="p-6 sm:p-8 lg:p-12 border-2 border-emerald-600/30 dark:border-primary-active/30 bg-gradient-to-br from-emerald-600/5 dark:from-primary-active/5 to-transparent">
              <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 items-center">
                <div>
                  <blockquote className="text-base sm:text-lg lg:text-xl italic text-muted-foreground dark:text-gray-300 mb-4 sm:mb-6">
                    "Before Euclideum, UDISE+ compliance was our biggest
                    administrative challenge. We spent weeks every year manually
                    entering data, dealing with errors, and managing APAAR IDs.
                    Now, everything is automated. What used to take us 200+
                    hours annually is now done in minutes with 100% accuracy.
                    Our teachers can focus on teaching instead of paperwork."
                  </blockquote>
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-emerald-600/20 dark:bg-primary-active/20 flex items-center justify-center">
                      <Users className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600 dark:text-primary-active" />
                    </div>
                    <div>
                      <div className="font-bold text-sm sm:text-base text-foreground dark:text-white">
                        Rajesh Kumar
                      </div>
                      <div className="text-xs sm:text-sm text-muted-foreground dark:text-gray-400">
                        IT Administrator, Walnut School
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
                  {[
                    { value: '2,500+', label: 'Students Managed' },
                    { value: '100%', label: 'Compliance Rate' },
                    { value: '200hrs', label: 'Saved Annually' },
                    { value: '24/7', label: 'Auto Sync' },
                  ].map((stat, index) => (
                    <Card
                      key={index}
                      className="p-3 sm:p-4 lg:p-6 text-center bg-card"
                    >
                      <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-emerald-600 dark:text-primary-active mb-1 sm:mb-2">
                        <AnimatedNumber
                          value={stat.value}
                          delay={index * 100}
                        />
                      </div>
                      <div className="text-xs sm:text-sm text-muted-foreground dark:text-gray-400">
                        {stat.label}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <Badge className="mb-4">FAQs</Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4 text-foreground dark:text-white">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-muted-foreground dark:text-gray-400 max-w-3xl mx-auto">
                Everything you need to know about our UDISE+ and APAAR
                integration
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto">
              {UDISE_APAAR_FAQS.map((faq) => (
                <details key={faq.question} className="group border-b">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 text-left [&::-webkit-details-marker]:hidden">
                    <h3 className="text-sm font-medium text-foreground">
                      {faq.question}
                    </h3>
                    <ChevronDown
                      aria-hidden
                      className="text-muted-foreground size-4 shrink-0 transition-transform duration-200 group-open:rotate-180 group-open:text-emerald-600 dark:group-open:text-primary-active"
                    />
                  </summary>
                  <p className="pb-4 text-sm text-muted-foreground dark:text-gray-400">
                    {faq.answer}
                  </p>
                </details>
              ))}
            </div>

            {/* Where a reader goes next. UDISE+ compliance is one part of the
                platform, so the trail out of this section names the pages that
                answer the questions this one does not. */}
            <div className="mx-auto mt-10 max-w-3xl text-sm leading-relaxed text-muted-foreground">
              Still deciding? See how UDISE+ reporting sits inside{' '}
              <Link
                href="/schoolerp"
                className="font-medium text-emerald-600 underline underline-offset-4 dark:text-primary-active"
              >
                Euclideum campus ERP software for schools and colleges
              </Link>
              , how the whole institution runs on the{' '}
              <Link
                href="/products/udc"
                className="font-medium text-emerald-600 underline underline-offset-4 dark:text-primary-active"
              >
                Unified Digital Campus platform
              </Link>
              , what the{' '}
              <Link
                href="/implementation"
                className="font-medium text-emerald-600 underline underline-offset-4 dark:text-primary-active"
              >
                campus ERP implementation process
              </Link>{' '}
              involves, and how to reach{' '}
              <Link
                href="/support"
                className="font-medium text-emerald-600 underline underline-offset-4 dark:text-primary-active"
              >
                product support after go-live
              </Link>
              .
            </div>
          </div>
        </div>
      </section>

      {/* Security & Compliance Badges */}
      <section className="py-12 bg-background">
        <div className="container px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-wrap items-center justify-center gap-8">
              {[
                { icon: ShieldCheck, text: 'ISO 27001 Certified' },
                { icon: Award, text: 'Government Approved' },
                { icon: Lock, text: 'Bank-grade Encryption' },
                { icon: Shield, text: 'Data Protection Protocols' },
                { icon: Cpu, text: 'Secure API Integration' },
                { icon: Cloud, text: '99.9% Uptime SLA' },
              ].map((badge, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center gap-2 text-muted-foreground dark:text-gray-400"
                >
                  <badge.icon className="w-5 h-5 text-emerald-600 dark:text-primary-active" />
                  <span className="font-medium">{badge.text}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 lg:py-24 bg-background relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--accent-green) / 0.3) 1px, transparent 1px)`,
              backgroundSize: '32px 32px',
            }}
          />
        </div>

        <div className="container px-6 lg:px-8 relative z-10">
          <div className="mx-auto max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 bg-emerald-600/10 dark:bg-primary-active/10 text-emerald-600 dark:text-primary-active border-emerald-600/30 dark:border-primary-active/30">
                Start Today
              </Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6 text-foreground">
                Make UDISE+ Compliance Your Competitive Advantage
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Join thousands of schools already achieving 100% compliance with
                zero effort. Transform your administrative efficiency today.
              </p>

              {/* Also previously dead buttons. */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button asChild size="lg" className="font-medium">
                  <Link href="/implementation">
                    Start Your UDISE+ Journey
                    <Rocket className="ml-2 size-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link href="/schedule-meeting">
                    Request for Demo
                    <ArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-muted-foreground">
                <a
                  href="tel:+918788474881"
                  className="flex items-center gap-2 hover:text-emerald-600 dark:hover:text-primary-active transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  <span>+91 8788474881</span>
                </a>
                <a
                  href="mailto:support@euclideum.com"
                  className="flex items-center gap-2 hover:text-emerald-600 dark:hover:text-primary-active transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  <span>support@euclideum.com</span>
                </a>
                <button className="flex items-center gap-2 hover:text-emerald-600 dark:hover:text-primary-active transition-colors">
                  <MessageSquare className="w-5 h-5" />
                  <span>Live Chat Support</span>
                </button>
              </div>

              <div className="mt-12 pt-8 border-t border-border dark:border-white/20">
                <p className="text-sm text-muted-foreground">
                  Trusted by leading educational institutions across India.
                  Government approved and fully compliant with NEP 2020
                  guidelines.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  )
}
